import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

export const fetchOrchestration = createAsyncThunk(
  'orchestration/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const [pipeline, stats, latencyResp] = await Promise.all([
        api.get('/api/v1/orchestration/pipeline').then(r => r.data),
        api.get('/api/v1/orchestration/stats').then(r => r.data),
        api.get('/api/v1/orchestration/latency').then(r => r.data),
      ])
      return { pipeline, stats, latency: latencyResp }
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

// ── Normalize backend stats response to frontend shape ────────────────────────
//
// Backend returns:
//   stats.rules.total / active / trigger_count
//   stats.transactions_24h.total / blocked / review / block_rate_pct
//   stats.latency.avg_ms / p95_ms / sla_met
//   stats.alerts.open / escalated
//
// Frontend reads flat keys:
//   stats.totalEvaluations, stats.avgLatencyMs, stats.p95LatencyMs,
//   stats.blockRate, stats.reviewRate, stats.allowRate, stats.rulesEvaluated
//   stats.stageBreakdown (array)
//
function normalizeStats(raw, pipeline) {
  if (!raw) return null

  const txn     = raw.transactions_24h || {}
  const latency = raw.latency          || {}
  const rules   = raw.rules            || {}
  const alerts  = raw.alerts           || {}

  const total    = txn.total    || 0
  const blocked  = txn.blocked  || 0
  const review   = txn.review   || 0
  const approved = txn.approved || 0

  const blockRate  = total > 0 ? +((blocked  / total) * 100).toFixed(1) : 0
  const reviewRate = total > 0 ? +((review   / total) * 100).toFixed(1) : 0
  const allowRate  = total > 0 ? +((approved / total) * 100).toFixed(1) : 0

  // stageBreakdown expected by the table — backend doesn't return per-stage detail yet
  // Build from pipeline.summary if available, otherwise empty
  const stageBreakdown = pipeline?.summary
  ? Object.entries(pipeline.summary).map(([stage, item]) => ({
      stage,
      evaluations: item.total || 0,
      blocks: item.active || 0,
      reviews: item.inactive || 0,
      avgMs: latency.avg_ms || 0,
    }))
  : []

  return {
    // Flat keys the UI reads
    totalEvaluations: total,
    avgLatencyMs:     latency.avg_ms  || 0,
    p95LatencyMs:     latency.p95_ms  || 0,
    blockRate:        txn.block_rate_pct != null ? txn.block_rate_pct : blockRate,
    reviewRate,
    allowRate,
    rulesEvaluated:   (rules.total_triggers || rules.active || 0) * total,
    slaMs:            latency.sla_ms  || 300,
    slaMet:           latency.sla_met ?? true,
    openAlerts:       alerts.open     || 0,
    escalatedAlerts:  alerts.escalated || 0,
    activeRules:      rules.active    || 0,
    totalRules:       rules.total     || 0,
    // Table
    stageBreakdown,
    // Keep raw for any direct access
    _raw: raw,
  }
}

// ── Normalize latency response ────────────────────────────────────────────────
//
// Backend returns: { summary: {...}, hourly: [{hour, p50, p95, p99, avg_ms}] }
// Frontend chart reads array with {label, p50, p95, p99}
//
function normalizeLatency(raw) {
  if (!raw) return []

  // If already an array, use as-is
  if (Array.isArray(raw)) return raw

  // New backend shape: { hourly: [...], summary: {...} }
  if (raw.hourly && Array.isArray(raw.hourly)) {
    return raw.hourly.map(h => ({
      label: h.hour     || h.hour_ts || '',
      p50:   h.p50      || 0,
      p95:   h.p95      || 0,
      p99:   h.p99      || 0,
      avg:   h.avg_ms   || 0,
    }))
  }

  return []
}

const orchestrationSlice = createSlice({
  name: 'orchestration',
  initialState: {
    pipeline: null,
    stats:    null,
    latency:  [],
    loading:  false,
    error:    null,
  },
  reducers: {},
  extraReducers: (b) => {
    b.addCase(fetchOrchestration.pending, (s) => {
      s.loading = true
      s.error   = null
    })
    b.addCase(fetchOrchestration.rejected, (s, a) => {
      s.loading = false
      s.error   = a.payload
    })
    b.addCase(fetchOrchestration.fulfilled, (s, a) => {
      s.loading = false
    
      s.pipeline = a.payload.pipeline
    
      s.stats = normalizeStats(
        a.payload.stats,
        a.payload.pipeline
      )
    
      s.latency = normalizeLatency(a.payload.latency)
    })
  },
})

export const selectOrchestration = (s) => s.orchestration

export default orchestrationSlice.reducer
